import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getChildren as getChildrenApi,
  getCalendarEvents as getCalendarEventsApi,
  createCalendarEvent as createCalendarEventApi,
  deleteCalendarEvent as deleteCalendarEventApi,
} from '../../util/api';

// ─── Async Thunks ───

const fetchChildrenThunk = createAsyncThunk(
  'SchedulePage/fetchChildren',
  async (_, { rejectWithValue }) => {
    try { return await getChildrenApi(); }
    catch (e: any) { return rejectWithValue(e.message || 'Failed to load children'); }
  }
);

const fetchEventsThunk = createAsyncThunk(
  'SchedulePage/fetchEvents',
  async (calendarId: string, { rejectWithValue }) => {
    try { return await getCalendarEventsApi(calendarId); }
    catch (e: any) { return rejectWithValue(e.message || 'Failed to load events'); }
  }
);

// ─── Slice ───

const slice = createSlice({
  name: 'SchedulePage',
  initialState: {
    children: [] as any[],
    fetchChildrenInProgress: false,
    fetchChildrenError: null as string | null,
    calendarEvents: [] as any[],
    fetchEventsInProgress: false,
    fetchEventsError: null as string | null,
    eventMutationInProgress: false,
    eventMutationError: null as string | null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchChildrenThunk.pending, s => { s.fetchChildrenInProgress = true; s.fetchChildrenError = null; })
      .addCase(fetchChildrenThunk.fulfilled, (s, a) => { s.fetchChildrenInProgress = false; s.children = a.payload; })
      .addCase(fetchChildrenThunk.rejected, (s, a) => { s.fetchChildrenInProgress = false; s.fetchChildrenError = a.payload as string; })

      .addCase(fetchEventsThunk.pending, s => { s.fetchEventsInProgress = true; s.fetchEventsError = null; })
      .addCase(fetchEventsThunk.fulfilled, (s, a) => { s.fetchEventsInProgress = false; s.calendarEvents = a.payload; })
      .addCase(fetchEventsThunk.rejected, (s, a) => { s.fetchEventsInProgress = false; s.fetchEventsError = a.payload as string; });
  },
});

export default slice.reducer;

// ─── Exported Thunks ───

export const fetchChildren = () => (dispatch: any) =>
  dispatch(fetchChildrenThunk()).unwrap();

export const fetchCalendarEvents = (calendarId: string) => (dispatch: any) =>
  dispatch(fetchEventsThunk(calendarId)).unwrap();

export const createEvent = (calendarId: string, eventData: any) => async (dispatch: any) => {
  await createCalendarEventApi({ calendarId, ...eventData });
  dispatch(fetchEventsThunk(calendarId));
};

export const removeEvent = (calendarId: string, eventUrl: string) => async (dispatch: any) => {
  await deleteCalendarEventApi({ calendarId, eventUrl });
  dispatch(fetchEventsThunk(calendarId));
};

// ─── loadData ───

export const loadData = () => (dispatch: any) => dispatch(fetchChildrenThunk());
